import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import type { GetOrganizationsOutput } from "@api/rest/types-and-hooks";
import { GLOBAL_ORG_LABEL, GLOBAL_ORG_SENTINEL } from "@components/Prompts/constants";

/** Sentinel value for the "match every organization" filter option.
 *  Only appears when the caller passes `allowAll`. Callers compare
 *  onChange against this to decide their filter behavior. Never sent
 *  to the API. */
export const ALL_ORGS_SENTINEL = "__all_orgs__";
export const ALL_ORGS_LABEL = "All Organizations";

interface OrgOwnerSelectProps {
  orgs: GetOrganizationsOutput[];
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** Overrides the label rendered above the selector. Defaults to
   *  `Organization owner`; a filter surface usually wants just
   *  `Organization`. */
  label?: string;
  /** When true, prepends an "All Organizations" entry above Global.
   *  The caller should treat `onChange(ALL_ORGS_SENTINEL)` as its
   *  match-every-org filter state. */
  allowAll?: boolean;
}

export function OrgOwnerSelect({ orgs, value, onChange, label, allowAll = false }: OrgOwnerSelectProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearchQuery("");
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const allItems = [
    ...(allowAll ? [{ id: ALL_ORGS_SENTINEL, name: ALL_ORGS_LABEL }] : []),
    { id: GLOBAL_ORG_SENTINEL, name: GLOBAL_ORG_LABEL },
    ...orgs.map((o) => ({ id: String(o.id), name: o.name })),
  ];

  const filtered = searchQuery.trim()
    ? allItems.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : allItems;

  // Which sentinel a bare null/undefined falls back to depends on the
  // mode: filter surfaces default to "All", owner-picker surfaces to
  // "Global" (an unowned profile).
  const defaultSentinel = allowAll ? ALL_ORGS_SENTINEL : GLOBAL_ORG_SENTINEL;
  const effectiveValue = value ?? defaultSentinel;
  const selectedItem = allItems.find((item) => item.id === effectiveValue);
  const isPlaceholder = effectiveValue === GLOBAL_ORG_SENTINEL || effectiveValue === ALL_ORGS_SENTINEL;
  // selectedItem can be undefined while orgs are still loading — don't fall back to a placeholder label.
  const displayName =
    selectedItem?.name ??
    (isPlaceholder ? (effectiveValue === ALL_ORGS_SENTINEL ? ALL_ORGS_LABEL : GLOBAL_ORG_LABEL) : "…");

  const handleSelect = (item: { id: string; name: string }) => {
    onChange(item.id);
    setDropdownOpen(false);
    setSearchQuery("");
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-text-secondary">{label ?? "Organization owner"}</span>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm hover:bg-bg-default"
          onClick={() => setDropdownOpen((prev) => !prev)}
        >
          <span className={clsx("truncate", isPlaceholder ? "italic text-text-tertiary" : "text-text-primary")}>
            {displayName}
          </span>
          <span className="ml-2 shrink-0 text-text-tertiary">▾</span>
        </button>

        {dropdownOpen && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-border-subtle bg-bg-surface shadow-md">
            <div className="sticky top-0 border-b border-border-subtle bg-bg-surface p-2">
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search organizations…"
                className="w-full rounded border border-border-medium px-2 py-1 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-500"
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-tertiary">No matches</div>
              ) : (
                filtered.map((item) => {
                  const isSelected = String(item.id) === String(effectiveValue);
                  const isSentinel = item.id === GLOBAL_ORG_SENTINEL || item.id === ALL_ORGS_SENTINEL;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={clsx(
                        "flex w-full items-center px-3 py-2 text-sm hover:bg-bg-default",
                        isSelected ? "font-medium text-text-primary" : "text-text-secondary",
                        isSentinel && !isSelected && "italic",
                      )}
                      onClick={() => handleSelect(item)}
                    >
                      {item.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
